import { useState, useMemo, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Box,
  Typography,
  useTheme,
  Chip
} from '@mui/material';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceDot, Area } from 'recharts';
import { tokens } from '../theme';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const GpxFitTraceabilityViewer = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get data from route state
  const gpxSegments = location.state?.gpxSegments || [];
  const fitChunks = location.state?.fitChunks || [];
  const workoutData = location.state?.workoutData;
  const [selectedGpxSegment, setSelectedGpxSegment] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [fitPaginationModel, setFitPaginationModel] = useState({ page: 0, pageSize: 25 });
  const apiRef = useGridApiRef();

  // Scroll to selected row when segment changes
  useEffect(() => {
    if (selectedGpxSegment && apiRef.current) {
      setTimeout(() => {
        try {
          // Use DataGrid API to scroll to the row
          apiRef.current.scrollToIndexes({ rowIndex: gpxSegments.findIndex(s => s.segment_id === selectedGpxSegment.segment_id) });
        } catch (error) {
          // Silently handle scroll errors
        }
      }, 500);
    }
  }, [selectedGpxSegment, paginationModel.page, gpxSegments, apiRef]);

  // GPX Segments columns
  const gpxColumns = [
    { 
      field: 'segment_id', 
      headerName: 'Segment ID', 
      width: 100,
      type: 'number'
    },
    { 
      field: 'distance_start', 
      headerName: 'Start (m)', 
      width: 110,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(1)
    },
    { 
      field: 'distance_end', 
      headerName: 'End (m)', 
      width: 110,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(1)
    },
    { 
      field: 'distance_length', 
      headerName: 'Length (m)', 
      width: 110,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(1)
    },
    { 
      field: 'avg_gradient', 
      headerName: 'Gradient (%)', 
      width: 120,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(2),
      cellClassName: (params) => {
        if (params.value > 5) return 'gradient-steep-climb';
        if (params.value > 2) return 'gradient-climb';
        if (params.value < -5) return 'gradient-steep-descent';
        if (params.value < -2) return 'gradient-descent';
        return 'gradient-flat';
      }
    },
    { 
      field: 'avg_power', 
      headerName: 'Power (W)', 
      width: 110,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(1)
    },
    { 
      field: 'avg_speed', 
      headerName: 'Speed (km/h)', 
      width: 120,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(2)
    },
    { 
      field: 'match_method', 
      headerName: 'Match Method', 
      width: 140,
      renderCell: (params) => {
        const colors = {
          'likely_chunks': 'success',
          'closest_chunks': 'warning',
          'no_fit_data': 'error'
        };
        return (
          <Chip 
            label={params.value || 'N/A'} 
            color={colors[params.value] || 'default'}
            size="small"
          />
        );
      }
    },
    { 
      field: 'match_tolerance', 
      headerName: 'Tolerance', 
      width: 100,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(2)
    },
    {
      field: 'matched_count',
      headerName: 'Matched Chunks',
      width: 130,
      type: 'number',
      valueGetter: (value, row) => row.matched_fit_chunk_indices?.length || 0
    },
    { 
      field: 'gpx_zone', 
      headerName: 'Zone', 
      width: 80,
      type: 'number',
      renderCell: (params) => {
        const zoneLabels = ['1st', '2nd', '3rd'];
        return zoneLabels[params.value] || 'N/A';
      }
    }
  ];

  // FIT Chunks columns (for matched chunks)
  const fitColumns = [
    { 
      field: 'chunk_index', 
      headerName: 'Chunk #', 
      width: 90,
      type: 'number'
    },
    { 
      field: 'timestamp', 
      headerName: 'Time (s)', 
      width: 100,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(1)
    },
    { 
      field: 'distance', 
      headerName: 'Distance (m)', 
      width: 120,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(1)
    },
    { 
      field: 'avg_gradient', 
      headerName: 'Gradient (%)', 
      width: 120,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(2),
      cellClassName: (params) => {
        if (params.value > 5) return 'gradient-steep-climb';
        if (params.value > 2) return 'gradient-climb';
        if (params.value < -5) return 'gradient-steep-descent';
        if (params.value < -2) return 'gradient-descent';
        return 'gradient-flat';
      }
    },
    { 
      field: 'avg_power', 
      headerName: 'Power (W)', 
      width: 110,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(1)
    },
    { 
      field: 'avg_speed', 
      headerName: 'Speed (km/h)', 
      width: 120,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(2)
    },
    { 
      field: 'assumed_cda', 
      headerName: 'CdA', 
      width: 90,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(3)
    },
    { 
      field: 'weight', 
      headerName: 'Weight', 
      width: 90,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(2),
      description: 'Zone-based weight applied to this chunk'
    },
    { 
      field: 'zone', 
      headerName: 'Zone', 
      width: 80,
      type: 'number',
      renderCell: (params) => {
        const zoneLabels = ['1st', '2nd', '3rd'];
        return zoneLabels[params.value] || 'N/A';
      }
    }
  ];

  // Prepare GPX rows with ID
  const gpxRows = useMemo(() => {
    if (!gpxSegments || !Array.isArray(gpxSegments)) return [];
    return gpxSegments.map((segment) => ({
      id: segment.segment_id,
      ...segment
    }));
  }, [gpxSegments]);

  // Get matched FIT chunks for selected GPX segment
  const getMatchedFitChunks = () => {
    if (!selectedGpxSegment || !selectedGpxSegment.matched_fit_chunk_indices) {
      return [];
    }

    const indices = selectedGpxSegment.matched_fit_chunk_indices;
    const weights = selectedGpxSegment.matched_fit_chunk_weights || [];
    
    return indices.map((chunkIndex, i) => {
      const chunk = fitChunks[chunkIndex];
      if (!chunk) return null;
      
      // Calculate zone for this chunk
      const totalChunks = fitChunks.length;
      const zoneSize = totalChunks / 3;
      const zone = Math.min(2, Math.floor(chunkIndex / zoneSize));
      
      return {
        id: chunkIndex,
        chunk_index: chunkIndex,
        timestamp: chunk.timestamp,
        distance: chunk.distance,
        avg_gradient: chunk.avg_gradient,
        avg_power: chunk.avg_power,
        avg_speed: chunk.avg_speed,
        assumed_cda: chunk.assumed_cda,
        weight: weights[i],
        zone: zone
      };
    }).filter(Boolean);
  };

  const matchedFitRows = useMemo(() => getMatchedFitChunks(), [selectedGpxSegment, fitChunks]);

  // Prepare chart data for GPX segments
  const gpxChartData = useMemo(() => {
    if (!gpxSegments) return [];
    
    let cumulativeDistance = 0;
    return gpxSegments.map((segment) => {
      const distanceKm = segment.distance_start / 1000; // Convert to km
      const dataPoint = {
        distance: distanceKm,
        altitude: segment.elevation_start,
        segment_id: segment.segment_id,
        gradient: segment.avg_gradient
      };
      return dataPoint;
    });
  }, [gpxSegments]);

  // Prepare chart data for FIT chunks
  const fitChartData = useMemo(() => {
    if (!fitChunks) return [];
    
    let cumulativeDistance = 0;
    return fitChunks.map((chunk, index) => {
      const distanceKm = cumulativeDistance / 1000; // Convert to km
      cumulativeDistance += chunk.distance;
      return {
        distance: distanceKm,
        altitude: chunk.avg_altitude,
        chunk_index: index,
        gradient: chunk.avg_gradient
      };
    });
  }, [fitChunks]);

  // Get highlighted points for selected segment
  const getHighlightedGpxPoint = () => {
    if (!selectedGpxSegment) return null;
    return {
      distance: selectedGpxSegment.distance_start / 1000,
      altitude: selectedGpxSegment.elevation_start
    };
  };

  // Get highlighted points for matched chunks
  const getHighlightedFitPoints = () => {
    if (!selectedGpxSegment || !selectedGpxSegment.matched_fit_chunk_indices) {
      return [];
    }
    
    return selectedGpxSegment.matched_fit_chunk_indices.map(index => {
      const dataPoint = fitChartData[index];
      return dataPoint ? {
        distance: dataPoint.distance,
        altitude: dataPoint.altitude,
        chunk_index: index
      } : null;
    }).filter(Boolean);
  };

  const highlightedGpxPoint = getHighlightedGpxPoint();
  const highlightedFitPoints = getHighlightedFitPoints();

  // Handle click on GPX chart to select segment
  const handleGpxChartClick = (data) => {
    if (data && data.activePayload && data.activePayload.length > 0) {
      const clickedSegmentId = data.activePayload[0].payload.segment_id;
      const segment = gpxSegments.find(s => s.segment_id === clickedSegmentId);
      if (segment) {
        setSelectedGpxSegment(segment);
        // Calculate which page the segment is on
        const segmentIndex = gpxSegments.findIndex(s => s.segment_id === clickedSegmentId);
        const targetPage = Math.floor(segmentIndex / paginationModel.pageSize);
        setPaginationModel({ ...paginationModel, page: targetPage });
      }
    }
  };

  return (
    <Box m="20px" display="flex" justifyContent="center">
      <Box maxWidth="1600px" width="100%">
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h3" fontWeight="bold" color={colors.grey[100]}>
            GPX-FIT Traceability Viewer
          </Typography>
          <Typography variant="body1" color={colors.grey[300]} mt={1}>
            Select a GPX segment to see which FIT chunks were used for matching
          </Typography>
        </Box>
        <Button 
          onClick={() => navigate('/analysis', { state: { workoutData } })} 
          variant="contained" 
          color="primary"
          startIcon={<ArrowBackIcon />}
        >
          Back
        </Button>
      </Box>

      <Box display="flex" flexDirection="column" gap={3}>
        {/* Altitude Charts Section - Moved to Top */}
        <Box display="flex" flexDirection="column" gap={2}>
          {/* GPX Altitude Chart */}
          <Box
            paddingTop={1}
            paddingBottom={1}
            sx={{
              backgroundColor: colors.grey[600],
              borderRadius: 2,
              boxShadow: 3,
            }}
          >
            <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={1} px={2}>
              GPX Altitude Profile
            </Typography>
            <Box height={400} bgcolor={colors.grey[800]} borderRadius="8px" p={2} mx={2}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={gpxChartData} onClick={handleGpxChartClick}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
                  <XAxis 
                    dataKey="distance" 
                    stroke={colors.grey[200]}
                    label={{ value: 'Distance (km)', position: 'insideBottom', offset: -5, fill: colors.grey[200] }}
                  />
                  <YAxis 
                    stroke={colors.grey[200]}
                    label={{ value: 'Altitude (m)', angle: -90, position: 'insideLeft', fill: colors.grey[200] }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: colors.grey[800], 
                      border: `1px solid ${colors.grey[700]}`,
                      borderRadius: '4px'
                    }}
                    labelStyle={{ color: colors.grey[100] }}
                    formatter={(value, name) => {
                      if (name === 'altitude') return [`${value.toFixed(1)} m`, 'Altitude'];
                      return [value, name];
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="altitude" 
                    stroke={colors.blueAccent[500]} 
                    dot={false}
                    strokeWidth={2}
                  />
                  {/* Highlight selected segment */}
                  {highlightedGpxPoint && (
                    <ReferenceDot
                      x={highlightedGpxPoint.distance}
                      y={highlightedGpxPoint.altitude}
                      r={8}
                      fill={colors.redAccent[500]}
                      stroke={colors.grey[100]}
                      strokeWidth={2}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          {/* FIT Altitude Chart */}
          <Box
            paddingTop={1}
            paddingBottom={1}
            sx={{
              backgroundColor: colors.grey[600],
              borderRadius: 2,
              boxShadow: 3,
            }}
          >
            <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={1} px={2}>
              FIT Altitude Profile
            </Typography>
            <Box height={400} bgcolor={colors.grey[800]} borderRadius="8px" p={2} mx={2}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={fitChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
                  <XAxis 
                    dataKey="distance" 
                    stroke={colors.grey[200]}
                    label={{ value: 'Distance (km)', position: 'insideBottom', offset: -5, fill: colors.grey[200] }}
                  />
                  <YAxis 
                    stroke={colors.grey[200]}
                    label={{ value: 'Altitude (m)', angle: -90, position: 'insideLeft', fill: colors.grey[200] }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: colors.grey[800], 
                      border: `1px solid ${colors.grey[700]}`,
                      borderRadius: '4px'
                    }}
                    labelStyle={{ color: colors.grey[100] }}
                    formatter={(value, name) => {
                      if (name === 'altitude') return [`${value.toFixed(1)} m`, 'Altitude'];
                      return [value, name];
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="altitude" 
                    stroke={colors.greenAccent[500]} 
                    dot={false}
                    strokeWidth={2}
                  />
                  {/* Highlight matched chunks */}
                  {highlightedFitPoints.map((point, index) => (
                    <ReferenceDot
                      key={`fit-${point.chunk_index}-${index}`}
                      x={point.distance}
                      y={point.altitude}
                      r={6}
                      fill={colors.redAccent[500]}
                      stroke={colors.grey[100]}
                      strokeWidth={2}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>

        {/* GPX Segments Grid */}
          <Box
            paddingTop={1}
            paddingBottom={1}
            sx={{
              backgroundColor: colors.grey[600],
              borderRadius: 2,
              boxShadow: 3,
            }}
          >
            <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={1} px={2}>
              GPX Segments ({gpxRows.length})
            </Typography>
            <Box 
              className="gpx-segments-grid"
              sx={{ 
                height: 500,
                width: '100%',
                px: 2,
                '& .gradient-steep-climb': {
                  color: colors.redAccent[500]
                },
                '& .gradient-climb': {
                  color: colors.redAccent[300]
                },
                '& .gradient-flat': {
                  color: colors.grey[200]
                },
                '& .gradient-descent': {
                  color: colors.greenAccent[400]
                },
                '& .gradient-steep-descent': {
                  color: colors.greenAccent[600]
                }
              }}
            >
              <DataGrid
                apiRef={apiRef}
                rows={gpxRows}
                columns={gpxColumns}
                onRowClick={(params) => setSelectedGpxSegment(params.row)}
                getRowClassName={(params) =>
                  selectedGpxSegment && params.row.segment_id === selectedGpxSegment.segment_id
                    ? 'selected-row'
                    : ''
                }
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 100 }
                  }
                }}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[25, 50, 100]}
                sx={{
                  border: 0,
                  color: `${colors.grey[200]} !important`,
                  '& .MuiDataGrid-cell': {
                    borderBottom: 'none'
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: colors.grey[800],
                    borderBottom: '2px solid ' + colors.grey[700]
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold'
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    backgroundColor: colors.grey[800]
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '2px solid ' + colors.grey[700],
                    backgroundColor: colors.grey[700]
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: colors.grey[700]
                  },
                  '& .selected-row': {
                    backgroundColor: colors.blueAccent[800],
                    '&:hover': {
                      backgroundColor: colors.blueAccent[700]
                    }
                  }
                }}
              />
            </Box>
          </Box>

          {/* Matched FIT Chunks Grid */}
          <Box
            paddingTop={1}
            paddingBottom={1}
            sx={{
              backgroundColor: colors.grey[600],
              borderRadius: 2,
              boxShadow: 3,
            }}
          >
            <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={1} px={2}>
              Matched FIT Chunks 
              {selectedGpxSegment && ` for Segment #${selectedGpxSegment.segment_id} (${matchedFitRows.length} chunks)`}
            </Typography>
            {selectedGpxSegment && (
              <Box mb={2} display="flex" gap={2} flexWrap="wrap" px={2}>
                <Chip 
                  label={`Method: ${selectedGpxSegment.match_method || 'N/A'}`} 
                  color="primary"
                />
                {selectedGpxSegment.match_tolerance && (
                  <Chip 
                    label={`Tolerance: ${selectedGpxSegment.match_tolerance.toFixed(2)}%`} 
                    color="secondary"
                  />
                )}
                <Chip 
                  label={`Base Power: ${selectedGpxSegment.base_avg_power?.toFixed(1) || 'N/A'}W`} 
                  color="default"
                />
                <Chip 
                  label={`Final Power: ${selectedGpxSegment.avg_power?.toFixed(1) || 'N/A'}W`} 
                  color="default"
                />
                {selectedGpxSegment.estimated_power && (
                  <Chip 
                    label={`Estimated Power: ${selectedGpxSegment.estimated_power.toFixed(1)}W`} 
                    color="warning"
                  />
                )}
                <Chip 
                  label={`Speed Method: ${selectedGpxSegment.speed_calculation_method || 'N/A'}`} 
                  color="info"
                />
              </Box>
            )}
            <Box 
              sx={{ 
                height: 500,
                width: '100%',
                px: 2,
                '& .gradient-steep-climb': {
                  color: colors.redAccent[500]
                },
                '& .gradient-climb': {
                  color: colors.redAccent[300]
                },
                '& .gradient-flat': {
                  color: colors.grey[200]
                },
                '& .gradient-descent': {
                  color: colors.greenAccent[400]
                },
                '& .gradient-steep-descent': {
                  color: colors.greenAccent[600]
                }
              }}
            >
              {!selectedGpxSegment || matchedFitRows.length === 0 ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                  bgcolor={colors.grey[800]}
                  borderRadius="4px"
                >
                  <Typography variant="body1" color={colors.grey[300]}>
                    {!selectedGpxSegment
                      ? 'Select a GPX segment above to view matched FIT chunks'
                      : 'No matched FIT chunks found for this segment'}
                  </Typography>
                </Box>
              ) : (
                <DataGrid
                  rows={matchedFitRows}
                  columns={fitColumns}
                  checkboxSelection={false}
                  initialState={{
                    pagination: {
                      paginationModel: { page: 0, pageSize: 25 }
                    }
                  }}
                  paginationModel={fitPaginationModel}
                  onPaginationModelChange={setFitPaginationModel}
                  pageSizeOptions={[25, 50, 100]}
                  sx={{
                    border: 0,
                    color: `${colors.grey[200]} !important`,
                    '& .MuiDataGrid-cell': {
                      borderBottom: 'none'
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[800],
                      borderBottom: '2px solid ' + colors.grey[700]
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 'bold'
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      backgroundColor: colors.grey[800]
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '2px solid ' + colors.grey[700],
                      backgroundColor: colors.grey[700]
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: colors.grey[700]
                    }
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
        </Box>
      </Box>
  );
};

export default GpxFitTraceabilityViewer;
